<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { twMerge } from 'tailwind-merge';
	import { CrossIcon } from '../../../../../../assets/icons/CrossIcon';
	import { HeniIcon } from '../../../../../../assets/icons/HeniIcon';
	import { HomeIcon } from '../../../../../../assets/icons/HomeIcon';
	import { Link } from '../../../../../../components/Link';
	import { Txt } from '../../../../../../components/Txt';
	import { toKebabCase } from '../../../../../../utils/toKebabCase';
	import type { GlobalNavLink, SiteNavLink } from '../../../../types';
	import type { NavPopover } from '../../../../utils/createNavPopover';
	import { isSiteLinkActive } from '../../../../utils/isSiteLinkActive/isSiteLinkActive';
	import { Socials } from '../../../Socials';
	import type { MobileMenuClasses } from '../MobileMenu.svelte';
	import { MobileMenuSiteLink } from '../MobileMenuSiteLink';
	import { MobileMenuDropdown } from './MobileMenuDropdown';

	interface Props {
		showGlobalNav?: boolean;
		siteLinks: SiteNavLink[];
		globalLinks: GlobalNavLink[];
		pathname: string;
		classes?: MobileMenuClasses;
		popover: NavPopover;
		dataCy: string;
		homeIcon?: import('svelte').Snippet;
		rightLink?: import('svelte').Snippet;
		copyright?: import('svelte').Snippet;
	}

	let {
		showGlobalNav = true,
		siteLinks,
		globalLinks,
		pathname,
		classes = {},
		popover,
		dataCy,
		homeIcon,
		rightLink,
		copyright
	}: Props = $props();

	const handleClose = () => {
		popover.close();
	};

	const isPathActive = (href?: string) => {
		if (!href?.startsWith('/')) return false;
		return pathname === href || pathname.startsWith(`${href}/`);
	};

	const siblingHrefs = $derived(siteLinks.map(({ href }) => href));

	const isGlobalLinkActive = (link: GlobalNavLink) =>
		isPathActive(link.href) || !!link.items?.some((item) => isPathActive(item.href));
</script>

<div
	class={twMerge(
		'bg-gray-0 flex w-full items-center justify-between px-4 py-3 sm:px-6 lg:hidden',
		classes.header
	)}
>
	{@render homeIcon?.()}
	<button onclick={handleClose} data-cy={`${dataCy}-close-icon`}>
		<CrossIcon title="Close navigation menu" />
	</button>
</div>

{#if siteLinks?.length}
	<div class={twMerge('bg-gray-0 relative w-full lg:hidden', classes.siteLinks)}>
		{#each siteLinks as { label, mobileLabel, href, newTab, onClick, items, show, showItemsOnlyOnMobile, shouldNotCloseOnClick }, index}
			{@const menuLabel = mobileLabel || label}
			{@const active = isSiteLinkActive({ pathname, href, siblingHrefs })}
			<div class="relative">
				<MobileMenuSiteLink
					{dataCy}
					{shouldNotCloseOnClick}
					{showItemsOnlyOnMobile}
					{href}
					{newTab}
					{active}
					{items}
					{onClick}
					{handleClose}
					label={menuLabel}
					class={twMerge(
						classNames({
							'border-t border-gray-200': index,
							hidden: show === 'desktop-only'
						}),
						classes.siteLink
					)}
				>
					{menuLabel}
				</MobileMenuSiteLink>

				{#if active}
					<div class="absolute bottom-0 left-0 top-0 h-full w-[0.125rem] bg-gray-900"></div>
				{/if}
			</div>
		{/each}

		{@render rightLink?.()}
	</div>
{:else}
	<div class={twMerge('relative w-full lg:hidden', classes.siteLinks)}>
		{@render rightLink?.()}
	</div>
{/if}

{#if showGlobalNav && globalLinks?.length}
	<div class={'w-full bg-gray-900'}>
		<!--
			The wrapper captures the bubbling click from the inner <Link> to close the menu.
			The Link itself is the real interactive element and handles keyboard activation;
			this wrapper is purely a passthrough side-effect handler.
		-->
		<!-- eslint-disable-next-line svelte/no-unused-svelte-ignore -->
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<!-- eslint-disable-next-line svelte/no-unused-svelte-ignore -->
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div onclick={handleClose}>
			<Link
				dataCy={`${dataCy}-home-link`}
				href="/"
				class={classNames(
					'flex h-[3.25rem] items-center justify-between border-b border-gray-700 px-4 sm:px-6 lg:hidden'
				)}
			>
				<HeniIcon color="gray-0" class="ml-[-0.4375rem] h-12 w-12" />
				<HomeIcon color="gray-0" class="mr-[-0.25rem]" />
			</Link>
		</div>

		{#each globalLinks as link}
			{@const active = isGlobalLinkActive(link)}
			<span class="w-full border-gray-700 outline-none lg:mb-1">
				{#if link.items?.length}
					<MobileMenuDropdown {link} {dataCy} {pathname} {active} onClose={handleClose} />
				{:else}
					<Link
						href={link.href}
						data-cy={`${dataCy}-${toKebabCase(link.label)}-link`}
						class={classNames(
							'flex items-center gap-2 border-b border-gray-700 px-4 py-4 active:bg-gray-700 sm:px-6',
							active && 'border-l-2 border-l-gray-0 pl-[0.875rem] sm:pl-[1.375rem]'
						)}
						onclick={handleClose}
						aria-current={active ? 'page' : undefined}
						{...link.newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}}
					>
						{#if link.isLive}
							<div
								class="h-[8px] w-[8px] rounded-full bg-green-300"
								data-cy={`${dataCy}-${toKebabCase(link.label)}-live-dot`}
							></div>
						{/if}
						<Txt variant="body2" class="text-gray-0 tracking-[.14em] uppercase">
							{link.label}
						</Txt>
					</Link>
				{/if}
			</span>
		{/each}
	</div>
{/if}

{#if showGlobalNav && globalLinks?.length}
	<Socials
		{dataCy}
		class={twMerge(
			'mt-6 justify-around sm:ml-[1.375rem] sm:justify-start sm:gap-6 lg:hidden',
			classes.socials
		)}
	/>
{/if}

<Txt
	class="p-8 pb-6 pl-6 text-center text-gray-500 sm:p-4 sm:pl-6 sm:text-left lg:hidden lg:pl-4"
	data-cy={`${dataCy}-copyright`}
	variant="body3"
>
	<span class="peer empty:hidden">
		{@render copyright?.()}
	</span>
	<span class="hidden peer-empty:inline">
		© {dayjs().format('YYYY')} HENI. All Rights Reserved.
	</span>
</Txt>
